import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { AuthStore } from '@huddle-sports-platform/app-core';

import { AccountStore } from './account-store';
import { AdminInviteStore } from './admin-invite-store';
import { ClubAdmin } from './club-admin';
import { ClubDataStore } from './club-data';
import { ClubPaymentsStore } from './club-payments';
import { MemberDataStore } from './member-data';
import { NotificationAccess } from './notification-access';

/**
 * DATA INITIALISATION - the one process that warms every signed-in
 * session, and the ONLY place that lists what "warmed" means.
 *
 * Every way a signed-in user exists - a refresh restoring the session, a
 * sign-in, a freshly created account - funnels through here before they
 * see a page: the root boot veil (`app.ts` `gateDone`) stays up until
 * {@link ready}, and every routing decision that reads this data
 * (`Landing`, `adminInviteGuard`, `clubAdminGuard`) awaits
 * {@link whenReady} first. One list, one veil, one answer.
 *
 * ## Adding a loader
 *
 * Add ONE line to {@link loaders}. The contract for an entry:
 *
 * - **Imperative and idempotent.** The method must START its own work on
 *   first call and be safe to call again (`whenLoaded()`-style). Guards
 *   run during the app's very first navigation, BEFORE Angular flushes
 *   root effects - a store whose first load is effect-driven deadlocks
 *   the router (see CLAUDE.md, 'the guard/effect deadlock').
 * - **Scoped to the signed-in uid.** The promise must answer for the
 *   account signed in NOW, not a predecessor's leftover snapshot - every
 *   current store already keys its cycle by uid.
 * - **Failure settles it.** A dead listener or a failed read must RESOLVE
 *   (fail open or closed as the store decides), never hang - a loader
 *   that waits forever holds the veil forever.
 *
 * Loaders run IN PARALLEL. Anything with an internal ordering need (the
 * invitation store waits for the club brand itself) owns that wait.
 *
 * ## What does NOT belong here
 *
 * - Paginated / on-demand stores (browse tables, feeds): they load when
 *   their page asks, by design.
 * - Presentation readiness (club brand, crest decode, the veil's minimum
 *   beat): those gate SIGNED-OUT doors too, so they live directly in
 *   `gateDone` alongside this service's `ready`.
 * - Mutations (the auto-join): `onboardedGuard` owns those - this process
 *   only READS.
 */
/**
 * How long ONE loader may hold the boot before the app opens without it.
 * Generous - this is a backstop against a hang, not a latency budget.
 */
const LOADER_TIMEOUT_MS = 12_000;

@Injectable({ providedIn: 'root' })
export class DataInit {
  private readonly auth = inject(AuthStore);
  private readonly account = inject(AccountStore);
  private readonly invites = inject(AdminInviteStore);
  private readonly clubAdmin = inject(ClubAdmin);
  private readonly notifications = inject(NotificationAccess);
  private readonly onboarding = inject(ClubPaymentsStore);
  private readonly clubData = inject(ClubDataStore);
  private readonly memberData = inject(MemberDataStore);

  /**
   * THE REGISTRY - everything a signed-in user needs before their first
   * page. Add new up-front loads here and nowhere else; the veil and every
   * guard pick them up automatically.
   */
  /** Loaders that failed or timed out on the last run - named, so a
   *  degraded boot is legible rather than mysterious. */
  private readonly _degraded = signal<readonly string[]>([]);
  readonly degraded = this._degraded.asReadonly();

  private readonly loaders: ReadonlyArray<{ name: string; settle: () => Promise<void> }> = [
    // The member's own accounts/{uid} doc - onboarding state, clubIds,
    // the claims stamp. The routing decision (profile vs tabs) reads it.
    { name: 'account', settle: () => this.account.whenLoaded() },
    // Pending invitations addressed to this login (realtime listener,
    // Callable fallback). Landing asks it FIRST - an unanswered offer
    // outranks both apps.
    { name: 'invitations', settle: () => this.invites.whenLoaded() },
    // The adminOf claim - whether this login runs the club, and so which
    // app (admin or member) opens.
    { name: 'club-admin', settle: () => this.clubAdmin.whenSettled() },
    // The device's notification permission - Landing sends anyone the
    // system would still ask to /enable-notifications first. Settles
    // instantly on the web (unsupported), and is device-level, so an
    // account switch reuses the answer.
    { name: 'notifications', settle: () => this.notifications.whenChecked() },
    // The club doc (payments verification) - the admin wizard trigger
    // and the member surfaces read it. Settles on failure (a fresh
    // sign-up cannot read it before the auto-join; the store's own
    // whenLoaded() retries after).
    { name: 'club', settle: () => this.onboarding.whenLoaded() },
    // The club's own subtree - twelve listeners behind every admin screen.
    // A no-op for a member (the store gates on the adminOf claim), so this
    // costs them nothing; for an admin it is the difference between the
    // admin area opening on live data and opening on skeletons.
    { name: 'club-data', settle: () => this.clubData.whenLoaded() },
    // The member's own footprint - what THIS login can see at the club:
    // their access doc and people, the membership plans, squads, the
    // events on sale, their tickets, asks, memberships and ledger. Every
    // member surface renders from it, live, with nothing left to fetch.
    { name: 'member-data', settle: () => this.memberData.whenLoaded() },
  ];

  /** The uid whose batch has settled; null-signed-out, undefined = never. */
  private readyFor: string | null | undefined;
  private inflight: Promise<void> | null = null;

  private readonly _ready = signal(false);
  /**
   * The signed-in user's up-front data is in - the boot veil's data-init
   * half. Flips false the moment a DIFFERENT account signs in (the veil
   * re-arms on the same uid change), true when that account's batch
   * settles. Meaningless while signed out - `gateDone` short-circuits.
   */
  readonly ready = this._ready.asReadonly();

  /** By uid, not user object: token refreshes re-emit the same uid and
   *  must not re-run initialisation mid-session. */
  private readonly uid = computed(() => this.auth.user()?.uid ?? null);

  constructor() {
    // Mid-session account changes (sign-in over a live app, sign-out).
    // The FIRST run after a cold load usually finds the batch already
    // started by whichever guard awaited whenReady() before effects
    // flushed - start() is idempotent, so both paths meet in the middle.
    effect(() => {
      const uid = this.uid();
      untracked(() => {
        if (uid === null) {
          // Signed out: nothing to init. Reset so the NEXT sign-in runs a
          // fresh batch rather than reusing the last account's answer.
          this.readyFor = null;
          this.inflight = null;
          this._ready.set(false);
          return;
        }
        void this.start(uid);
      });
    });
  }

  /**
   * Resolves once the account signed in NOW has its up-front data.
   *
   * Callable before anything else has run (it starts the batch itself),
   * and while signed OUT (it waits - at boot, guards can run before
   * Firebase restores the persisted session; either the restore lands and
   * the batch answers, or the auth guards tear the navigation down).
   */
  async whenReady(): Promise<void> {
    for (;;) {
      const uid = this.uid();
      if (uid !== null) {
        if (this.readyFor === uid) return;
        await this.start(uid);
        if (this.readyFor === uid) return;
        // An account switch landed mid-batch - loop and settle for the
        // uid that is signed in now.
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  /**
   * One loader's turn, which can NEVER take the app down with it.
   *
   * Two things are guarded, because either one used to be fatal:
   *
   * - **A rejection.** `Promise.all` rejects on the first failure, and the
   *   rejection travelled out through `whenReady()` into the landing guard,
   *   where a failed navigation leaves the boot veil up for ever. One
   *   loader that throws should cost its own data, not the whole app.
   * - **A silence.** A loader that neither resolves nor rejects held the
   *   batch open with nothing to show for it.
   *
   * Either way the loader is recorded in {@link degraded} and the batch
   * carries on: the app opens on the data it does have, the stores keep
   * retrying underneath, and the console names what was missed instead of
   * the user staring at a spinner.
   */
  private async settleOne(loader: { name: string; settle: () => Promise<void> }): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        loader.settle(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error(`timed out after ${LOADER_TIMEOUT_MS}ms`)),
            LOADER_TIMEOUT_MS,
          );
        }),
      ]);
    } catch (error) {
      this._degraded.update((names) => [...names, loader.name]);
      console.error(`[data-init] ${loader.name} did not settle - opening without it`, error);
    } finally {
      clearTimeout(timer);
    }
  }

  /** Run the batch for `uid` - once; concurrent callers share the run. */
  private start(uid: string): Promise<void> {
    if (this.readyFor === uid) return Promise.resolve();
    if (this.inflight) return this.inflight;
    this._ready.set(false);
    this._degraded.set([]);
    this.inflight = Promise.all(this.loaders.map((l) => this.settleOne(l)))
      .then(() => {
        // Answer for the account that is STILL signed in - a switch during
        // the batch means the new uid's effect run re-enters start().
        if (this.uid() === uid) {
          this.readyFor = uid;
          this._ready.set(true);
        }
      })
      .finally(() => {
        this.inflight = null;
      });
    return this.inflight;
  }
}
